// @flow
import * as React from 'react';
import type { RackDetailedRepresentation } from 'esm-web-pro/types/networks';
import type { Map } from 'esm-web-pro/containers/map/types';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import { Box } from 'grommet';
import RackIcon from 'esm-web-pro/components/layout/sidePanel/icons/RackIcon';
import { injectIntl, IntlShape } from 'react-intl';
import { point12Messages } from 'esm-web-pro/messages/points12Messages';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import { withRouter } from 'react-router-dom';
import type { Match, RouterHistory } from 'react-router-dom';
import RackAttributes from 'esm-web-pro/containers/pages/network/objects/rack/RackAttributes';
import RackActions from 'esm-web-pro/containers/pages/network/objects/rack/RackActions';
import RackContent from 'esm-web-pro/containers/pages/network/objects/rack/RackContent';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import { useLeaflet } from 'react-leaflet';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import SidePane from 'esm-web-pro/components/layout/SidePane';

type Props = {
    panelId: String,
    object: RackDetailedRepresentation,
    map: Map,
    editor: BaseEditor,
    updateObject: (data: $Shape<RackDetailedRepresentation>) => void,
    resource: string,
    intl: IntlShape,
    match: Match,
    history: RouterHistory
};

const Rack = (props: Props) => {
    const {
        intl: { formatMessage }, object: rack, match, updateObject, resource, editor, map, history, panelId,
    } = props;

    const leaflet = useLeaflet();

    const numberPointCallback = React.useCallback(
        UseHighlightPoint(rack.pop.coordinates.coordinates, rack.pop.alias || rack.pop.sequence), [rack],
    );

    const handleView = React.useCallback(() => {
        editor.unregisterAllLoadingCallback();
        editor.registerLoadingCallback(numberPointCallback);
        if (leaflet.map) {
            const coords = rack.pop.coordinates.coordinates;
            leaflet.map.highlightedGeometry = {
                bounds: null, center: [coords[1], coords[0]],
            };
            leaflet.map.panTo([coords[1], coords[0]]);
        }
    }, [editor, leaflet, numberPointCallback, rack]);

    React.useEffect(() => {
        handleView();

        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, [handleView, editor]);

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={rack.sequence}
            titleLabel={formatMessage(point12Messages.rack)}
            titleIcon={<RackIcon />}
            titleOnView={handleView}
            actions={<NavigateBackButton panelId={panelId} />}
        >
            <Box height="100%" justify="between">
                <Box flex>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <RackAttributes rack={rack} url={match.url} onView={handleView} />
                        <HorizontalLine color="grey" />
                        <Box direction="row">
                            <RackActions
                                rack={rack}
                                updateObject={updateObject}
                                resource={resource}
                                editor={editor}
                                map={map}
                                onEdit={updateObject}
                            />
                        </Box>
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                    <BottomContainer>
                        <RackContent rack={rack} />
                    </BottomContainer>
                </Box>
            </Box>
        </SidePane>
    );
};

export default withRouter(injectIntl(Rack));
